import { useEffect, useState } from 'react';
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, PieChart, Pie, Cell, Legend } from 'recharts';
import { api } from '../lib/api';

const COLORS = ['#6366f1', '#8b5cf6', '#a78bfa', '#c4b5fd', '#ddd6fe'];

interface Analytics {
  users: { total: number; active: number };
  aiRequests: { total: number };
  costs: { totalUsd: number };
  tokens: { totalSpent: number };
  modelBreakdown: { model: string; count: number; totalTokens: number; totalCost: string }[];
}

function StatCard({ label, value, sub }: { label: string; value: string | number; sub?: string }) {
  return (
    <div className="bg-white rounded-lg border p-5">
      <p className="text-sm text-gray-500">{label}</p>
      <p className="text-3xl font-bold mt-1 tabular-nums">{value}</p>
      {sub && <p className="text-xs text-gray-400 mt-1">{sub}</p>}
    </div>
  );
}

export default function Analytics() {
  const [data, setData] = useState<Analytics | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    api.getAnalytics()
      .then(setData)
      .catch((e) => setError(String(e)))
      .finally(() => setLoading(false));
  }, []);

  if (loading) return <p className="text-gray-500">Loading analytics...</p>;
  if (error) return <p className="text-red-500">{error}</p>;
  if (!data) return null;

  return (
    <div>
      <h1 className="text-2xl font-bold mb-6">Analytics</h1>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
        <StatCard label="Total users" value={data.users.total} />
        <StatCard label="Active users" value={data.users.active} />
        <StatCard label="AI requests" value={data.aiRequests.total} />
        <StatCard
          label="Total AI cost"
          value={`$${data.costs.totalUsd.toFixed(4)}`}
          sub={`${data.tokens.totalSpent.toLocaleString()} tokens spent`}
        />
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {/* Model usage bar chart */}
        <div className="bg-white rounded-lg border p-5">
          <h2 className="text-base font-semibold mb-4">Requests by model</h2>
          <ResponsiveContainer width="100%" height={220}>
            <BarChart data={data.modelBreakdown}>
              <XAxis dataKey="model" tick={{ fontSize: 11 }} />
              <YAxis tick={{ fontSize: 11 }} />
              <Tooltip />
              <Bar dataKey="count" fill="#6366f1" radius={[4, 4, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>

        {/* Cost by model pie chart */}
        <div className="bg-white rounded-lg border p-5">
          <h2 className="text-base font-semibold mb-4">Cost by model (USD)</h2>
          <ResponsiveContainer width="100%" height={220}>
            <PieChart>
              <Pie
                data={data.modelBreakdown}
                dataKey="totalCost"
                nameKey="model"
                cx="50%"
                cy="50%"
                outerRadius={80}
                label={({ model, percent }: { model: string; percent: number }) =>
                  `${model.split('/')[1]} ${(percent * 100).toFixed(0)}%`
                }
              >
                {data.modelBreakdown.map((_, i) => (
                  <Cell key={i} fill={COLORS[i % COLORS.length]} />
                ))}
              </Pie>
              <Legend />
            </PieChart>
          </ResponsiveContainer>
        </div>
      </div>
    </div>
  );
}
